/**
 * OracleMemoryTab.jsx - The Sacred Memory Archive
 * 
 * Displays past conversation summaries with:
 * - Delete functionality
 * - "Manifest the Vision" ritualistic image generation
 * - Cross-oracle memory awareness
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Sparkles, Share2, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getAuthHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Ritualistic loading messages
const LOADING_MESSAGES = {
    en: [
        "Consulting the Astral Plane...",
        "Weaving the threads of your revelation...",
        "The Oracle channels your vision...",
        "Manifesting from the cosmic void..."
    ],
    es: [
        "Consultando el Plano Astral...",
        "Tejiendo los hilos de tu revelación...",
        "El Oráculo canaliza tu visión...",
        "Manifestando desde el vacío cósmico..."
    ]
};

const OracleMemoryTab = ({ oracleType, onClose }) => {
    const { t, language } = useLanguage();
    const [memories, setMemories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [manifestingId, setManifestingId] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [error, setError] = useState(null);
    
    const isSpanish = language?.startsWith('es');
    
    // Fetch memories on mount
    useEffect(() => {
        fetchMemories();
    }, [oracleType]);
    
    // Rotate loading messages during manifestation
    useEffect(() => {
        if (manifestingId) {
            const messages = isSpanish ? LOADING_MESSAGES.es : LOADING_MESSAGES.en;
            let index = 0;
            setLoadingMessage(messages[0]);
            
            const interval = setInterval(() => {
                index = (index + 1) % messages.length;
                setLoadingMessage(messages[index]);
            }, 2500);
            
            return () => clearInterval(interval);
        }
    }, [manifestingId, isSpanish]);
    
    const fetchMemories = async () => {
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(
                `${API_URL}/api/oracle/memories${oracleType ? `?oracle_type=${oracleType}` : ''}`,
                {
                    headers,
                    credentials: 'include'
                }
            );
            
            if (response.ok) {
                const data = await response.json();
                setMemories(data.memories || []);
            }
        } catch (e) {
            console.error('Failed to fetch memories:', e);
        } finally {
            setLoading(false);
        }
    };
    
    const handleDelete = async (memoryId) => {
        const confirmText = isSpanish 
            ? '¿Estás seguro de que deseas eliminar esta memoria?' 
            : 'Are you sure you want to delete this memory?';
        
        if (!window.confirm(confirmText)) return;
        
        try {
            const headers = await getAuthHeaders();
            console.log('[Memory Delete] Deleting memory:', memoryId);
            
            const response = await fetch(`${API_URL}/api/oracle/memories/${encodeURIComponent(memoryId)}`, {
                method: 'DELETE',
                headers,
                credentials: 'include'
            });
            
            if (response.ok) {
                console.log('[Memory Delete] Success, removing from list');
                setMemories(prev => prev.filter(m => m.id !== memoryId));
            } else {
                const errorText = await response.text();
                console.error('[Memory Delete] Failed:', response.status, errorText);
            }
        } catch (e) {
            console.error('Failed to delete memory:', e);
        }
    };
    
    const handleManifestVision = async (memory) => {
        if (memory.has_image) {
            setSelectedImage(memory.image_url);
            return;
        }
        
        setManifestingId(memory.id);
        setError(null);
        
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_URL}/api/oracle/manifest-vision?lang=${language}`, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: JSON.stringify({
                    memory_id: memory.id,
                    summary_text: memory.summary,
                    oracle_type: memory.oracle_type
                })
            });
            
            // Read response body as text first, then parse
            const responseText = await response.text();
            
            if (response.ok) {
                try {
                    const data = JSON.parse(responseText);
                    // Update memory with new image
                    setMemories(prev => prev.map(m => 
                        m.id === memory.id 
                            ? { ...m, has_image: true, image_url: data.image_url }
                            : m
                    ));
                    setSelectedImage(data.image_url);
                } catch {
                    setError(isSpanish ? 'Error al procesar respuesta' : 'Error processing response');
                }
            } else {
                // Parse error from response text
                try {
                    const errorData = JSON.parse(responseText);
                    setError(errorData.detail || (isSpanish ? 'Error al manifestar la visión' : 'Failed to manifest vision'));
                } catch {
                    setError(isSpanish ? 'Error al manifestar la visión' : 'Failed to manifest vision');
                }
            }
        } catch (e) {
            console.error('Failed to manifest vision:', e);
            setError(isSpanish ? 'Error al manifestar la visión' : 'Failed to manifest vision');
        } finally {
            setManifestingId(null);
        }
    };
    
    const handleShare = async (imageUrl) => {
        try {
            // Convert base64 to blob for sharing
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const file = new File([blob], 'astral-vision.png', { type: 'image/png' });
            
            if (navigator.share && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: 'My Astral Vision',
                    text: isSpanish ? 'Mi visión del Oráculo de Astral Belonging' : 'My Oracle vision from Astral Belonging'
                });
            } else {
                // Fallback: download
                const link = document.createElement('a');
                link.href = imageUrl;
                link.download = 'astral-vision.png';
                link.click();
            }
        } catch (e) {
            console.error('Share failed:', e);
        }
    };
    
    const formatDate = (dateStr) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString(isSpanish ? 'es-ES' : 'en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };
    
    const getOracleIcon = (type) => {
        switch (type) {
            case 'cosmos': return '✨';
            case 'love': return '💕';
            case 'dream': return '🌙';
            default: return '🔮';
        }
    };
    
    const getOracleName = (type) => {
        const names = {
            cosmos: isSpanish ? 'Cosmos' : 'Cosmos',
            love: isSpanish ? 'Amor' : 'Love',
            dream: isSpanish ? 'Sueños' : 'Dreams'
        };
        return names[type] || type;
    };

    return (
        <div className="h-full flex flex-col bg-background/95">
            {/* Header */}
            <div className="p-4 border-b border-primary/10">
                <h2 className="font-cinzel text-lg text-primary">
                    {isSpanish ? 'Memorias Sagradas' : 'Sacred Memories'}
                </h2>
                <p className="text-xs text-primary/50 mt-1">
                    {isSpanish 
                        ? 'Tus revelaciones pasadas, guardadas en el éter' 
                        : 'Your past revelations, preserved in the ether'}
                </p>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 text-primary/50 animate-spin" />
                    </div>
                ) : memories.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-primary/40 text-sm">
                            {isSpanish 
                                ? 'Aún no hay memorias guardadas' 
                                : 'No memories saved yet'}
                        </p>
                        <p className="text-primary/30 text-xs mt-2">
                            {isSpanish 
                                ? 'Tus conversaciones con el Oráculo aparecerán aquí' 
                                : 'Your conversations with the Oracle will appear here'}
                        </p>
                    </div>
                ) : (
                    <AnimatePresence>
                        {memories.map((memory, index) => (
                            <motion.div
                                key={memory.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, x: -100 }}
                                transition={{ delay: index * 0.05 }}
                                className="bg-background border border-primary/10 rounded-lg p-4"
                            >
                                {/* Memory Header */}
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span>{getOracleIcon(memory.oracle_type)}</span>
                                        <span className="text-xs font-medium text-primary/70">
                                            {getOracleName(memory.oracle_type)}
                                        </span>
                                        <span className="text-xs text-primary/40">
                                            {formatDate(memory.created_at)}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => handleDelete(memory.id)}
                                        className="p-1.5 text-primary/30 hover:text-red-400 transition-colors"
                                        title={isSpanish ? 'Eliminar' : 'Delete'}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                                
                                {/* Summary */}
                                <p className="text-sm text-primary/80 leading-relaxed mb-3">
                                    {memory.summary}
                                </p>
                                
                                {/* Actions */}
                                <div className="flex items-center gap-2">
                                    {manifestingId === memory.id ? (
                                        <div className="flex items-center gap-2 text-primary/60">
                                            <Loader2 size={14} className="animate-spin" />
                                            <span className="text-xs italic">{loadingMessage}</span>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => handleManifestVision(memory)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${
                                                memory.has_image
                                                    ? 'bg-primary/10 text-primary hover:bg-primary/20'
                                                    : 'bg-gradient-to-r from-primary/20 to-amber-500/20 text-primary hover:from-primary/30 hover:to-amber-500/30'
                                            }`}
                                        >
                                            {memory.has_image ? (
                                                <>
                                                    <ImageIcon size={12} />
                                                    {isSpanish ? 'Ver Visión' : 'View Vision'}
                                                </>
                                            ) : (
                                                <>
                                                    <Sparkles size={12} />
                                                    {isSpanish ? 'Manifestar la Visión' : 'Manifest the Vision'}
                                                </>
                                            )}
                                        </button>
                                    )}
                                    
                                    {memory.has_image && (
                                        <button
                                            onClick={() => handleShare(memory.image_url)}
                                            className="flex items-center gap-1 px-2 py-1.5 text-primary/50 hover:text-primary text-xs transition-colors"
                                        >
                                            <Share2 size={12} />
                                            {isSpanish ? 'Compartir' : 'Share'}
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                )}
                
                {error && (
                    <div className="text-center py-4">
                        <p className="text-red-400/70 text-sm">{error}</p>
                    </div>
                )}
            </div>
            
            {/* Disclaimer */}
            <div className="p-3 border-t border-primary/10 text-center">
                <p className="text-xs text-primary/30">
                    {isSpanish 
                        ? 'El Oráculo concede una sola visión por revelación' 
                        : 'The Oracle grants one vision per revelation'}
                </p>
            </div>
            
            {/* Image Preview Modal */}
            <AnimatePresence>
                {selectedImage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
                        onClick={() => setSelectedImage(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="relative max-w-lg w-full"
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setSelectedImage(null)}
                                className="absolute -top-10 right-0 text-white/70 hover:text-white"
                            >
                                <X size={24} />
                            </button>
                            
                            <img
                                src={selectedImage}
                                alt="Oracle Vision"
                                className="w-full rounded-lg shadow-2xl"
                            />
                            
                            <div className="mt-4 flex justify-center gap-4">
                                <button
                                    onClick={() => handleShare(selectedImage)}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary/20 text-primary rounded-full hover:bg-primary/30 transition-colors"
                                >
                                    <Share2 size={16} />
                                    {isSpanish ? 'Compartir' : 'Share'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default OracleMemoryTab;
